/**
 * Route to Line Converter
 * 
 * Utility functions to convert Fixed Routes to Atlas Line features
 * and vice versa.
 */

import type { FixedRoute, FixedRouteStop, FixedRouteLineMetadata, RouteGeometry } from '../../types/fixedRoutes';
import type { Line, Point } from '../../context/context';
import { calculateRouteGeometryForStops as osrmCalculateRouteGeometryForStops, calculateRouteGeometry as osrmCalculateRouteGeometry } from '../../services/routing/osrmClient';
import { calculateRouteGeometryForStops as hereCalculateRouteGeometryForStops, calculateRouteGeometry as hereCalculateRouteGeometry } from '../../services/routing/hereClient';

/**
 * Generate a distinct color for a route based on its ID
 * Uses a simple hash function to generate consistent colors
 */
export function generateRouteColor(routeId: string): string {
  let hash = 0;
  for (let i = 0; i < routeId.length; i++) {
    hash = routeId.charCodeAt(i) + ((hash << 5) - hash);
  }

  // Generate colors using HSL with good saturation and lightness
  const hue = Math.abs(hash) % 360;
  return `hsl(${hue}, 70%, 50%)`;
}

/**
 * Generate a hash of stop IDs and coordinates for cache validation
 */
export function generateStopsHash(routeStops: FixedRouteStop[]): string {
  return routeStops
    .map((stop) => `${stop.id}:${stop.coordinates?.lat || 0}:${stop.coordinates?.lng || 0}`)
    .join('|');
}

function areCoordsClose(a: [number, number], b: [number, number], eps: number = 1e-6): boolean {
  return Math.abs(a[0] - b[0]) <= eps && Math.abs(a[1] - b[1]) <= eps;
}

function dedupeConsecutiveCoords(points: [number, number][], eps: number = 1e-6): [number, number][] {
  const out: [number, number][] = [];
  for (const p of points) {
    const last = out[out.length - 1];
    if (!last || !areCoordsClose(last, p, eps)) {
      out.push(p);
    }
  }
  return out;
}

function distanceToSegmentSquared(
  point: [number, number],
  segStart: [number, number],
  segEnd: [number, number]
): { distanceSq: number; t: number } {
  const ax = segStart[0];
  const ay = segStart[1];
  const bx = segEnd[0];
  const by = segEnd[1];
  const px = point[0];
  const py = point[1];

  const abx = bx - ax;
  const aby = by - ay;
  const apx = px - ax;
  const apy = py - ay;
  const abLenSq = abx * abx + aby * aby;
  if (abLenSq <= 1e-12) {
    const dx = px - ax;
    const dy = py - ay;
    return { distanceSq: dx * dx + dy * dy, t: 0 };
  }

  const rawT = (apx * abx + apy * aby) / abLenSq;
  const t = Math.max(0, Math.min(1, rawT));
  const cx = ax + t * abx;
  const cy = ay + t * aby;
  const dx = px - cx;
  const dy = py - cy;
  return { distanceSq: dx * dx + dy * dy, t };
}

type EffectiveWaypointChainResult = {
  chain: [number, number][];
  repairedWaypointStopIndices?: number[];
};

/**
 * Build a stop-anchored routing chain.
 * - Stops are authoritative anchors and always appear in order.
 * - Waypoints are control points inserted between stop pairs.
 */
function buildEffectiveWaypointChain(route: FixedRoute, routeStops: FixedRouteStop[]): EffectiveWaypointChainResult {
  const controlWaypoints = route.waypoints ?? [];
  const waypointStopIndices = route.waypointStopIndices ?? [];
  const stopCoords: [number, number][] = routeStops.map((s) => [s.coordinates!.lat, s.coordinates!.lng]);
  if (stopCoords.length === 0) return { chain: [] };
  if (stopCoords.length === 1) return { chain: [stopCoords[0]] };
  if (controlWaypoints.length === 0) return { chain: stopCoords };

  // Preferred ordering mode: explicit segment anchors produced during capture/edit.
  // Each waypoint is assigned to a stop segment index and rendered in click order within that segment.
  if (waypointStopIndices.length === controlWaypoints.length) {
    const segmentBuckets: Array<Array<{ point: [number, number]; order: number }>> =
      Array.from({ length: stopCoords.length - 1 }, () => []);
    let hadClampRepair = false;
    const repairedIndices: number[] = [];

    controlWaypoints.forEach((wp, order) => {
      const rawIndex = waypointStopIndices[order] ?? 0;
      const segIdx = Math.max(0, Math.min(stopCoords.length - 2, rawIndex));
      repairedIndices.push(segIdx);
      if (segIdx !== rawIndex) hadClampRepair = true;
      segmentBuckets[segIdx].push({ point: wp, order });
    });

    const chain: [number, number][] = [stopCoords[0]];
    for (let segIdx = 0; segIdx < stopCoords.length - 1; segIdx++) {
      const bucket = segmentBuckets[segIdx];
      bucket.sort((a, b) => a.order - b.order);
      bucket.forEach((entry) => chain.push(entry.point));
      chain.push(stopCoords[segIdx + 1]);
    }

    return {
      chain: dedupeConsecutiveCoords(chain),
      repairedWaypointStopIndices: hadClampRepair ? repairedIndices : undefined,
    };
  }

  // Fallback mode for legacy routes without segment anchors:
  // infer segment assignment by nearest stop segment with monotonic progression.
  const segmentBuckets: Array<Array<{ point: [number, number]; order: number; t: number }>> =
    Array.from({ length: stopCoords.length - 1 }, () => []);

  let minSegmentIndex = 0;
  const inferredIndices: number[] = [];
  controlWaypoints.forEach((wp, order) => {
    let bestSegment = minSegmentIndex;
    let bestDistSq = Number.POSITIVE_INFINITY;
    let bestT = 0;

    for (let segIdx = minSegmentIndex; segIdx < stopCoords.length - 1; segIdx++) {
      const segStart = stopCoords[segIdx];
      const segEnd = stopCoords[segIdx + 1];
      const { distanceSq, t } = distanceToSegmentSquared(wp, segStart, segEnd);
      if (distanceSq < bestDistSq) {
        bestDistSq = distanceSq;
        bestSegment = segIdx;
        bestT = t;
      }
    }

    minSegmentIndex = bestSegment;
    inferredIndices.push(bestSegment);
    segmentBuckets[bestSegment].push({ point: wp, order, t: bestT });
  });

  const chain: [number, number][] = [stopCoords[0]];
  for (let segIdx = 0; segIdx < stopCoords.length - 1; segIdx++) {
    const bucket = segmentBuckets[segIdx];
    bucket.sort((a, b) => (a.t === b.t ? a.order - b.order : a.t - b.t));
    bucket.forEach((entry) => chain.push(entry.point));
    chain.push(stopCoords[segIdx + 1]);
  }

  return {
    chain: dedupeConsecutiveCoords(chain),
    repairedWaypointStopIndices: inferredIndices,
  };
}

function getEffectiveWaypointChain(route: FixedRoute, routeStops: FixedRouteStop[]): [number, number][] {
  return buildEffectiveWaypointChain(route, routeStops).chain;
}

/**
 * Compute the geometry cache hash for a route.
 *
 * IMPORTANT: This hash is treated as the contract between:
 * - geometry producers (route geometry calculators / converters)
 * - geometry consumers (map sync logic deciding whether cached geometry is valid)
 *
 * It MUST change whenever the expected rendered geometry should change.
 */
export function computeRouteGeometryHash(route: FixedRoute, routeStops: FixedRouteStop[]): string {
  const stopsHash = generateStopsHash(routeStops);

  // Include core routing modifiers that change geometry.
  // Keep the first segment as the stop-hash so consumers can parse stop IDs/counts reliably.
  const isCirculator = route.isCirculator ? 1 : 0;
  const isBusRoute = route.isBusRoute ? 1 : 0;
  const freehand = route.freehand ? 1 : 0;
  // Waypoints shape the rendered geometry, so a waypoint move/insert/remove MUST invalidate it.
  const waypointsSig = (route.waypoints ?? []).map((w) => `${w[0].toFixed(5)},${w[1].toFixed(5)}`).join(';');
  const waypointIdxSig = (route.waypointStopIndices ?? []).join(',');

  return `${stopsHash}::c=${isCirculator}::b=${isBusRoute}::f=${freehand}::w=${waypointsSig}::wi=${waypointIdxSig}`;
}

/** Returns the stop-hash portion (before the first `::`). */
export function getStopsHashPart(geometryHash: string): string {
  const idx = geometryHash.indexOf('::');
  return idx === -1 ? geometryHash : geometryHash.slice(0, idx);
}

function mergeSegmentGeometries(
  segmentGeometries: [number, number][][],
  returnSegmentGeometry?: [number, number][]
): [number, number][] {
  const allSegments = returnSegmentGeometry
    ? [...segmentGeometries, returnSegmentGeometry]
    : segmentGeometries;

  let coordinates: [number, number][] = [];
  for (const segment of allSegments) {
    if (coordinates.length > 0 && segment.length > 0) {
      const lastPoint = coordinates[coordinates.length - 1];
      const firstPoint = segment[0];
      if (
        Math.abs(lastPoint[0] - firstPoint[0]) < 0.0001 &&
        Math.abs(lastPoint[1] - firstPoint[1]) < 0.0001
      ) {
        coordinates = [...coordinates, ...segment.slice(1)];
      } else {
        coordinates = [...coordinates, ...segment];
      }
    } else {
      coordinates = [...coordinates, ...segment];
    }
  }

  return coordinates;
}

function collectAffectedWaypointLegIndices(
  previousChain: [number, number][],
  nextChain: [number, number][]
): number[] {
  if (previousChain.length !== nextChain.length || previousChain.length < 2) {
    return [];
  }

  const affected = new Set<number>();
  for (let i = 0; i < nextChain.length; i++) {
    if (!areCoordsClose(previousChain[i], nextChain[i])) {
      if (i > 0) affected.add(i - 1);
      if (i < nextChain.length - 1) affected.add(i);
    }
  }

  return Array.from(affected).sort((a, b) => a - b);
}

async function calculateWaypointLegSegment(
  route: FixedRoute,
  waypointChain: [number, number][],
  legIndex: number
): Promise<[number, number][]> {
  const fromCoords = waypointChain[legIndex];
  const toCoords = waypointChain[legIndex + 1];
  const fromStop: FixedRouteStop = {
    id: `waypoint-from-${legIndex}`,
    name: `Waypoint ${legIndex + 1}`,
    coordinates: { lat: fromCoords[0], lng: fromCoords[1] },
  };
  const toStop: FixedRouteStop = {
    id: `waypoint-to-${legIndex + 1}`,
    name: `Waypoint ${legIndex + 2}`,
    coordinates: { lat: toCoords[0], lng: toCoords[1] },
  };

  try {
    const segment = route.isBusRoute
      ? await hereCalculateRouteGeometry(fromStop, toStop, true)
      : await osrmCalculateRouteGeometry(fromStop, toStop, 'driving');

    if (segment && segment.length > 0) {
      return segment;
    }

    console.warn(
      `[FixedRoutes] Empty waypoint segment ${legIndex} for route ${route.id}; using straight segment fallback.`
    );
    return [
      [fromCoords[0], fromCoords[1]],
      [toCoords[0], toCoords[1]],
    ];
  } catch (error) {
    console.warn(
      `[FixedRoutes] Failed waypoint segment ${legIndex} for route ${route.id}; using straight segment fallback.`,
      error
    );
    return [
      [fromCoords[0], fromCoords[1]],
      [toCoords[0], toCoords[1]],
    ];
  }
}

/**
 * Convert a Fixed Route to an Atlas Line feature
 * This includes calculating route geometry if not already calculated
 */
export async function convertRouteToLine(
  route: FixedRoute,
  stops: FixedRouteStop[],
  fixedRoutesLayerId?: number
): Promise<Line> {
  // Get stop coordinates in route order, filtering out stops with invalid coordinates
  const routeStops = route.stops
    .map((stopConfig) => stops.find((s) => s.id === stopConfig.stopId))
    .filter((stop): stop is FixedRouteStop => {
      if (!stop) return false;
      // Validate stop has valid coordinates
      const coords = stop.coordinates;
      const lat = coords?.lat;
      const lng = coords?.lng;
      return coords && 
             lat !== undefined && 
             lng !== undefined &&
             lat !== null && 
             lng !== null &&
             typeof lat === 'number' && 
             typeof lng === 'number' &&
             !isNaN(lat) && 
             !isNaN(lng) &&
             isFinite(lat) &&
             isFinite(lng);
    });

  if (routeStops.length < 2) {
    throw new Error('Route must have at least 2 stops');
  }

  const currentStopsHash = computeRouteGeometryHash(route, routeStops);
  // Calculate route geometry if not already cached or if cached geometry is invalid
  let coordinates: [number, number][] = [];
  let computedGeometry: RouteGeometry | undefined;

  if (
    route.geometry?.coordinates &&
    route.geometry.coordinates.length > 0 &&
    route.geometry.stopsHash === currentStopsHash
  ) {
    // Use cached geometry - it's still valid for current stops
    coordinates = route.geometry.coordinates;
    computedGeometry = route.geometry;
  } else if (route.freehand) {
    // Freehand: literal straight lines between stops, no road snapping / routing.
    const chain: [number, number][] = routeStops.map((s) => [s.coordinates.lat, s.coordinates.lng]);
    const segGeoms: [number, number][][] = [];
    for (let i = 0; i < chain.length - 1; i++) {
      segGeoms.push([chain[i], chain[i + 1]]);
    }
    let freehandReturn: [number, number][] | undefined;
    if (route.isCirculator && chain.length >= 2) {
      freehandReturn = [chain[chain.length - 1], chain[0]];
      chain.push(chain[0]); // close the loop
    }
    coordinates = chain;
    computedGeometry = {
      coordinates,
      calculatedAt: Date.now(),
      stopsHash: currentStopsHash,
      segmentGeometries: segGeoms,
      returnSegmentGeometry: freehandReturn,
      waypointChain: undefined,
      waypointLegGeometries: undefined,
      repairedWaypointStopIndices: undefined,
    };
  } else {
    let segments: [number, number][][];
    let stopSegmentGeometries: [number, number][][] | undefined;
    let returnSegmentGeometry: [number, number][] | undefined;
    if (route.isBusRoute) {
      // Use HERE for bus routes
      segments = await hereCalculateRouteGeometryForStops(routeStops, true);
    } else {
      // Use OSRM for regular routes
      segments = await osrmCalculateRouteGeometryForStops(routeStops, 'driving');
    }
    
    // For circulator routes, add return segment from last stop to first stop
    if (route.isCirculator && routeStops.length >= 2) {
      const lastStop = routeStops[routeStops.length - 1];
      const firstStop = routeStops[0];
      
      try {
        const returnSegment = route.isBusRoute
          ? await hereCalculateRouteGeometry(lastStop, firstStop, true)
          : await osrmCalculateRouteGeometry(lastStop, firstStop, 'driving');
        
        if (returnSegment && returnSegment.length > 0) {
          returnSegmentGeometry = returnSegment;
        } else {
          // Don't create fallback - throw error instead to prevent straight-line routes
          throw new Error(`Failed to calculate return segment for circulator route ${route.id}. Route will not be displayed.`);
        }
        
        // Minimal rate limiting: wait 100ms (reduced from 1100ms for faster rendering)
        await new Promise((resolve) => setTimeout(resolve, 100));
      } catch (error) {
        console.error('Failed to calculate return segment for circulator route:', error);
        // Don't create fallback - rethrow to prevent straight-line routes
        throw error;
      }
    }

    stopSegmentGeometries = segments.slice(0, routeStops.length - 1);
    coordinates = mergeSegmentGeometries(stopSegmentGeometries, returnSegmentGeometry);
    
    // Ensure circulator routes form a closed loop by connecting last point to first point
    if (route.isCirculator && coordinates.length > 0) {
      const firstPoint = coordinates[0];
      const lastPoint = coordinates[coordinates.length - 1];
      
      // If the last point doesn't match the first point, add it to close the loop
      if (
        Math.abs(lastPoint[0] - firstPoint[0]) > 0.0001 ||
        Math.abs(lastPoint[1] - firstPoint[1]) > 0.0001
      ) {
        coordinates.push([firstPoint[0], firstPoint[1]]);
      }
    }

    computedGeometry = {
      coordinates,
      calculatedAt: Date.now(),
      stopsHash: currentStopsHash,
      segmentGeometries: stopSegmentGeometries,
      returnSegmentGeometry,
      waypointChain: undefined,
      waypointLegGeometries: undefined,
      repairedWaypointStopIndices: undefined,
    };
  }

  // Generate route color if not set
  const routeColor = route.color || generateRouteColor(route.id);

  // Create metadata for Fixed Route identification
  const fixedRouteMetadata: FixedRouteLineMetadata = {
    type: 'fixedRoute',
    fixedRouteId: route.id,
    routeName: route.name,
    shortName: route.shortName,
    isCirculator: route.isCirculator,
  };

  // Create Atlas Line
  // CRITICAL: Always use fixedRoutesLayerId if provided, otherwise use route.layerId
  // This ensures routes are always in the Fixed Routes layer, not other layers
  const line: Line = {
    id: route.lineId || `fixed-route-${route.id}`,
    layerId: fixedRoutesLayerId ?? route.layerId,
    type: 'line',
    name: route.name,
    description: `Fixed Route: ${route.name}`,
    colour: routeColor,
    opacity: '1.0', // Full opacity for prominence
    borderColour: routeColor,
    borderWidth: route.width ?? 6, // Use route width if specified, default to 6
    isLocked: false,
    coordinates,
    dashPattern: route.isCirculator ? '10, 5' : 'none',
    arrowStyle: 'start',
    hidden: route.hidden || false,
    // Store metadata in a way that doesn't conflict with Line type
    ...({ fixedRouteMetadata } as any),
    // Internal handoff for map sync to persist geometry cache on the route.
    ...({ fixedRouteComputedGeometry: computedGeometry } as any),
  };

  return line;
}

/**
 * Convert an Atlas Line back to Fixed Route if it's a fixed route line
 */
export function convertLineToRoute(line: Line, stops: FixedRouteStop[]): FixedRoute | null {
  const metadata = (line as any).fixedRouteMetadata as FixedRouteLineMetadata | undefined;

  if (!metadata || metadata.type !== 'fixedRoute') {
    return null;
  }

  // Extract stop IDs from coordinates (this is a simplified approach)
  // In a real implementation, you'd need to store stop references in the line
  // For now, we'll create a basic route structure
  const route: FixedRoute = {
    id: metadata.fixedRouteId,
    name: metadata.routeName || line.name,
    startTime: '08:00', // Default - would need to be stored
    stops: [], // Would need to reconstruct from coordinates
    segments: [], // Would need to reconstruct
    isCirculator: metadata.isCirculator,
    hidden: line.hidden || false,
    lineId: line.id,
    layerId: line.layerId,
    color: line.colour,
    geometry: {
      coordinates: line.coordinates,
      calculatedAt: Date.now(),
      stopsHash: '', // Would need to calculate
    },
  };

  return route;
}

/**
 * Convert HSL to hex color
 */
function hslToHex(h: number, s: number, l: number): string {
  l /= 100;
  const a = (s * Math.min(l, 1 - l)) / 100;
  const f = (n: number) => {
    const k = (n + h / 30) % 12;
    const color = l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
    return Math.round(255 * color)
      .toString(16)
      .padStart(2, '0');
  };
  return `#${f(0)}${f(8)}${f(4)}`.toUpperCase();
}

/**
 * Generate color palette for multiple routes.
 * Returns hex colors for better compatibility.
 *
 * Uses golden-angle hue spacing (~137.5°) so that consecutively-assigned
 * colors are always far apart on the colour wheel. A plain `360 / count`
 * step collapses to near-identical hues for large imports (e.g. 78 GTFS
 * routes => ~4.6° apart, visually indistinguishable) and, because colours
 * are assigned in route order, neighbouring routes ended up nearly the same
 * shade. The golden angle never repeats and keeps adjacent entries distinct
 * regardless of `count`. Saturation/lightness are also cycled to further
 * separate hues that eventually land close together.
 */
export function generateRouteColors(count: number): string[] {
  const colors: string[] = [];
  const GOLDEN_ANGLE = 137.508;
  // Cycle through a few S/L combinations so that even hues that drift close
  // after many turns of the wheel still read as different colours.
  const saturations = [72, 58, 85];
  const lightnesses = [48, 62, 38];

  for (let i = 0; i < count; i++) {
    const hue = (i * GOLDEN_ANGLE) % 360;
    const saturation = saturations[i % saturations.length];
    const lightness = lightnesses[Math.floor(i / saturations.length) % lightnesses.length];
    colors.push(hslToHex(hue, saturation, lightness));
  }

  return colors;
}

